import React, { useState } from "react";
import swal from "sweetalert";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Paper,
} from "@mui/material";
import { FilterBar, ReportHeader, currencyFmt } from "./ReportsPages";
import { getGroupLiquidity } from "../../services/reportService";
import { useTranslation } from "react-i18next";

export function GroupLiquidityReport() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ from: "", to: "", group: "" });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!filters.group) {
      swal(t("Missing Filter"), t("Please select a group to generate the report."), "warning");
      return;
    }
    setLoading(true);
    setError(null);
    setReportData(null);
    try {
      const data = await getGroupLiquidity(filters.group);
      setReportData(data);
    } catch (err) {
      setError(err.message);
      swal(t("Error"), err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => swal(t("Info"), t("Export logic not yet implemented."), "info");

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <ReportHeader
        title={t("Group Liquidity Position")}
        subtitle={t("Cash availability versus outstanding loan obligations.")}
        roleHint={t("Access: Chairperson / Secretary / Accountant / Admin / Desk Officer")}
      />

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onGenerate={handleGenerate}
        isGenerating={loading}
        onExport={handleExport}
        showGroup
      />

      {loading && <Typography>{t("Loading report...")}</Typography>}
      {error && <Typography color="error">{t("Error")}: {error}</Typography>}

      {!reportData && !loading && (
        <Typography color="text.secondary" align="center" sx={{ my: 4 }}>{t("Please select a group and click \"Generate\" to view the report.")}</Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t("Cash on Hand")}
              </Typography>
              <Typography variant="h6" fontWeight={900} color="text.secondary">
                {currencyFmt(reportData?.cash_balance)} TZS
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t("Group Bank Balance")}
              </Typography>
              <Typography variant="h6" fontWeight={900}>
                {currencyFmt(reportData?.group_account_balance)} TZS
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t("Liquidity Index")}
              </Typography>
              <Typography variant="h6" fontWeight={900}>
                {(parseFloat(reportData?.liquidity_index || 0) * 100).toFixed(0)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("Higher is better. Below 50% is a risk signal.")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {reportData && (
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
              {t("Obligations vs Available Cash")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {t("Obligations")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("Outstanding Loans")}: {currencyFmt(reportData?.total_outstanding_loans)} TZS
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("Overdue Loans")}: {currencyFmt(reportData?.overdue_loans)} TZS
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("Expected Repayments (month)")}:{" "}
                    {currencyFmt(reportData?.expected_repayments_next_30_days)} TZS
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    minHeight: 140,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                    {t("Simple Liquidity Visual")}
                  </Typography>
                  {/* Placeholder for chart library */}
                  <Box
                    sx={{
                      width: "80%",
                      height: 16,
                      borderRadius: 999,
                      bgcolor: "#eee",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${(parseFloat(reportData?.liquidity_index || 0)) * 100}%`,
                        height: "100%",
                        bgcolor:
                          parseFloat(reportData?.liquidity_index || 0) >= 0.8
                            ? "success.main"
                            : parseFloat(reportData?.liquidity_index || 0) >= 0.5
                              ? "warning.main"
                              : "error.main",
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    {t("Integrate a proper chart (e.g. Recharts) later.")}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
